import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Headset } from "lucide-react";
import { useCustomerAuth } from "@/lib/customerAuth";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/how-it-works", label: "How it works" },
  { to: "/rates", label: "Rates" },
  { to: "/accepted-cards", label: "Cards" },
  { to: "/blog", label: "Spark" },
  { to: "/faq", label: "FAQ" },
  { to: "/refer", label: "Refer & earn" },
];

function Brand({ onClick }) {
  return (
    <Link
      to="/"
      data-testid="nav-logo"
      className="lm-brand"
      onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 16, textDecoration: "none" }}
    >
      <img
        src="/jumbo/images/gc-spark-mark.png"
        alt=""
        aria-hidden="true"
        style={{ height: 88, width: 88, objectFit: "contain", display: "block", flexShrink: 0 }}
      />
      <span
        className="lm-brand-name"
        style={{
          fontFamily: "Satoshi, sans-serif",
          fontWeight: 800,
          fontSize: 44,
          letterSpacing: "-0.015em",
          color: "#0b0d17",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        GC <span className="lm-brand-spark">SPAR<span className="lm-brand-k">
          K
          <svg className="lm-brand-k-star" viewBox="0 0 24 24" aria-hidden="true">
            <defs>
              <linearGradient id="lm-k-star" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ffd76a" />
                <stop offset="60%" stopColor="#f5a524" />
                <stop offset="100%" stopColor="#c77b0a" />
              </linearGradient>
            </defs>
            <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill="url(#lm-k-star)" />
          </svg>
        </span></span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { customer } = useCustomerAuth();
  const close = () => setOpen(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header data-testid="site-navbar" className="lm-header">
      <div className="lm-header-inner">
        <div className="navigation-container" style={{ display: "flex", alignItems: "center", height: 112, padding: "0 14px 0 22px" }}>
          <div className="navigation-left" style={{ width: "auto" }}>
            <Brand />
          </div>
          <div className="navigation-right" style={{ flex: 1 }}>
            <nav className="nav-menu" style={{ justifyContent: "center", gap: 4 }}>
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="navigation-link w-nav-link"
                  style={({ isActive }) => ({
                    color: isActive ? "#346af8" : "#09000f99",
                    fontWeight: isActive ? 600 : 400,
                    padding: "10px 10px",
                    whiteSpace: "nowrap",
                  })}
                >
                  {n.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("gc:open-human-support"))}
                data-testid="nav-human-support"
                className="navigation-link w-nav-link"
                style={{ color: "#09000f99", fontWeight: 400, padding: "10px 10px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
              >
                <Headset size={16} /> Human support
              </button>
            </nav>
            <div className="nav_button_box" style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
              <Link
                to={customer ? "/dashboard" : "/signin"}
                data-testid="nav-login"
                style={{ color: "#0b0d17", fontWeight: 600, fontFamily: '"DM Sans", sans-serif', fontSize: 17, textDecoration: "none", whiteSpace: "nowrap" }}
              >
                {customer ? "Dashboard" : "Sign in"}
              </Link>
              <Link to="/submit" data-testid="nav-submit-cta" className="lm_button w-inline-block" style={{ padding: "13px 26px", fontSize: 17, whiteSpace: "nowrap" }}>
                <div>Sell a card</div>
              </Link>
            </div>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            className="lg:hidden"
            style={{ marginLeft: "auto", width: 44, height: 44, borderRadius: 12, border: "none", background: "#0b0d170d", color: "#0b0d17", display: "none", alignItems: "center", justifyContent: "center" }}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" style={{ position: "fixed", inset: "92px 14px auto", zIndex: 1100, background: "#fff", borderRadius: 24, border: "1px solid #09000f14", padding: 18, boxShadow: "0 30px 70px -30px rgba(20,20,50,.4)" }}>
          <nav onClick={close} style={{ display: "flex", flexDirection: "column" }}>
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className="navigation-link" style={{ padding: "14px 6px", borderBottom: "1px solid #09000f0f", color: "#0b0d17", fontSize: 17 }}>
                {n.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("gc:open-human-support"))}
              data-testid="nav-mobile-human-support"
              className="navigation-link"
              style={{ textAlign: "left", padding: "14px 6px", borderBottom: "1px solid #09000f0f", color: "#0b0d17", fontSize: 17, background: "none", border: "none", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", width: "100%" }}
            >
              <Headset size={18} /> Human support
            </button>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, paddingTop: 16 }}>
              <Link to={customer ? "/dashboard" : "/signin"} style={{ height: 48, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 90, background: "#0b0d170d", color: "#0b0d17", fontWeight: 600, textDecoration: "none" }}>
                {customer ? "Dashboard" : "Sign in"}
              </Link>
              <Link to="/submit" className="lm_button w-inline-block" style={{ justifyContent: "center" }}>
                <div>Sell a card</div>
              </Link>
            </div>
          </nav>
        </div>
      )}

      <style>{`
        @media (max-width: 991px) {
          .lm-header .nav-menu, .lm-header .nav_button_box { display: none !important; }
          .lm-header [data-testid="nav-mobile-toggle"] { display: inline-flex !important; }
        }
      `}</style>
    </header>
  );
}
